/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useSelector } from "react-redux"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { BenefitType, Subsidy } from "#/lib/carrot-api/types/Subsidy"
import { getCompanyCurrencyCode } from "#/redux/reducers/companyInfo"
import { getCategoryCoverage } from "#/utils/CategoryCoverage"
import { BalanceSection, BalanceTile, BalanceTileRow, CarrotCycles } from "../../molecules/BalanceTiles"
import { YourResponsibilityCard } from "../../../containers/planinfo/YourResponsibilityCard"
import { CoveredExpensesCard } from "./PayingForCareModal"

const plainText = (chunks: React.ReactNode) => chunks

const FundMaximumTiles = ({ fund }: { fund: Subsidy }) => {
  const intl = useIntl()
  const currencyCode = useSelector(getCompanyCurrencyCode)
  const yearlyMax = fund.yearlyAmounts.max
  const lifetimeMax = fund.lifetimeAmounts.max
  const formatMax = (amount: number) =>
    intl.formatNumber(amount / 100, {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0
    })
  // hasUnlimitedBenefit only means there is no annual max, so it can't tell whether the lifetime max is capped.
  if (yearlyMax === null && lifetimeMax === null) {
    return (
      <BalanceTile
        title={<FormattedMessage defaultMessage="Lifetime max" />}
        value={intl.formatMessage({ defaultMessage: "Unlimited" })}
      />
    )
  }
  return (
    <>
      {yearlyMax !== null && (
        <BalanceTile title={<FormattedMessage defaultMessage="Annual max" />} value={formatMax(yearlyMax)} />
      )}
      {lifetimeMax !== null && (
        <BalanceTile title={<FormattedMessage defaultMessage="Lifetime max" />} value={formatMax(lifetimeMax)} />
      )}
    </>
  )
}

const CreditMaximumTile = ({ credit }: { credit: Subsidy }) => {
  const intl = useIntl()
  return (
    <BalanceTile
      title={<FormattedMessage defaultMessage="Lifetime max" />}
      value={intl.formatMessage(
        { defaultMessage: "{count, plural, one {# credit} other {# credits}}" },
        { count: credit.creditLifetimeMax }
      )}
    />
  )
}

const CreditAndFundMaximums = ({ credit, fund }: { credit: Subsidy; fund: Subsidy }) => (
  <>
    <BalanceSection
      titleVariant="h3"
      gutterBottom={false}
      title={<FormattedMessage defaultMessage="Credits" />}
      description={
        <FormattedMessage
          defaultMessage="{cycleSentence}You can <link>use credits</link> for eligible fertility services at eligible providers."
          values={{ link: plainText, cycleSentence: <CarrotCycles creditLifetimeMax={credit.creditLifetimeMax} /> }}
        />
      }
    >
      <CreditMaximumTile credit={credit} />
    </BalanceSection>
    <BalanceSection
      titleVariant="h3"
      gutterBottom={false}
      title={<FormattedMessage defaultMessage="Additional funds" />}
      description={
        <FormattedMessage
          defaultMessage="You can <link>use funds</link> for eligible services not covered by your credits."
          values={{ link: plainText }}
        />
      }
    >
      <FundMaximumTiles fund={fund} />
    </BalanceSection>
  </>
)

// group is one category's plans, credit first; guideSubsidies is every category the benefit guide shows.
export const BenefitGuideCategoryDetails = ({
  group,
  guideSubsidies,
  singleCategory = false
}: {
  group: Subsidy[]
  guideSubsidies: Subsidy[]
  singleCategory?: boolean
}): JSX.Element => {
  const theme = useTheme()
  const credit = group.find((subsidy) => subsidy.benefitType === BenefitType.CREDIT)
  const fund = group.find((subsidy) => subsidy.benefitType !== BenefitType.CREDIT)
  const category = group[0].reimbursementCategoryNameMessageKey
  // Without a financial benefit the API sends no plan dates and empty maximums, so there is nothing to show.
  const hasFinancialBenefit = group[0].planStartDate != null

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
      {credit && fund ? (
        <CreditAndFundMaximums credit={credit} fund={fund} />
      ) : (
        hasFinancialBenefit && (
          <BalanceTileRow>
            {credit ? <CreditMaximumTile credit={credit} /> : <FundMaximumTiles fund={fund} />}
          </BalanceTileRow>
        )
      )}
      <CoveredExpensesCard
        category={category}
        coverage={getCategoryCoverage(guideSubsidies)}
        singleCategory={singleCategory}
      />
      <YourResponsibilityCard subsidy={fund ?? credit} allSubsidies={guideSubsidies} />
    </Stack>
  )
}
